import { Button, toast } from '@bia-auction/ui';
import { t } from '@bia-auction/shared-i18n';
import { useEffect, useState } from 'react';
import {
  disablePush,
  enablePush,
  isPushConfigured,
  isPushEnabled,
  isPushSupported,
} from '../lib/push';

/**
 * Push opt-in toggle (M8, ADR 0021). Shown only to a logged-in user (the shell gates it) and
 * only when the browser supports the Push API AND the server has Web Push configured — otherwise
 * it renders nothing, so a user never clicks an apparently-available control that can only fail.
 * Enabling registers the service worker + subscribes; disabling unsubscribes and clears the
 * stored subscription. All the failure modes are surfaced as toasts, never thrown.
 */
export function NotificationBell() {
  const [supported] = useState(() => isPushSupported());
  // null = still probing the server config; false = unconfigured (hide); true = available.
  const [available, setAvailable] = useState<boolean | null>(null);
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!supported) return;
    let active = true;
    void isPushConfigured().then((ok) => {
      if (!active) return;
      setAvailable(ok);
      if (ok) void isPushEnabled().then((e) => active && setEnabled(e));
    });
    return () => {
      active = false;
    };
  }, [supported]);

  // Hide until we know push is both supported here and configured server-side.
  if (!supported || available !== true) return null;

  const onToggle = async () => {
    setBusy(true);
    try {
      if (enabled) {
        await disablePush();
        setEnabled(false);
        toast(t('bidder.notif.disabledToast'), 'success');
        return;
      }
      const result = await enablePush();
      if (result === 'enabled') {
        setEnabled(true);
        toast(t('bidder.notif.enabledToast'), 'success');
      } else if (result === 'blocked') {
        toast(t('bidder.notif.blocked'), 'error');
      } else if (result === 'failed') {
        toast(t('bidder.notif.failed'), 'error');
      }
      // 'unconfigured' / 'unsupported' — nothing to tell the user; leave the toggle off.
    } finally {
      setBusy(false);
    }
  };

  const label = enabled ? t('bidder.notif.disable') : t('bidder.notif.enable');
  return (
    <Button
      variant="outline"
      size="sm"
      onClick={onToggle}
      disabled={busy}
      title={label}
      aria-label={label}
    >
      {enabled ? '🔔' : '🔕'}
    </Button>
  );
}
